/**
 * momentConstants.ts
 *
 * 카카오 모먼트 공통 상수 모음.
 *
 * 동일하거나 유사한 상수가 MomentListPage, MomentReservePage, MomentReportPage에
 * 중복 정의되어 있었습니다. 이 파일에서 단일 관리합니다.
 *
 * 참고: DATE_PRESET_OPTIONS(목록 페이지용 4개)와
 *       REPORT_DATE_PRESET_OPTIONS(리포트 페이지용 3개)는
 *       항목 수와 라벨이 달라 별도 상수로 관리합니다.
 */

// ── 광고 상태 ─────────────────────────────────────────────────

/** 광고 상태 코드 → 한글 레이블 */
export const STATUS_LABEL: Record<string, string> = {
  DRAFT: "임시저장",
  READY: "발송대기",
  SENDING: "발송중",
  FINISHED: "발송완료",
  CANCELED: "발송취소",
  ADMIN_STOP: "관리자정지",
  KAKAO_ADMIN_STOP: "관리자정지",
};

/**
 * 광고 상태 코드 → Tailwind 배지 클래스.
 * 리스트 페이지에서 상태 뱃지 색상을 결정할 때 사용합니다.
 */
export const STATUS_BADGE: Record<string, string> = {
  DRAFT: "tw-bg-gray-100 tw-text-gray-600",
  READY: "tw-bg-amber-100 tw-text-amber-700",
  SENDING: "tw-bg-sky-100 tw-text-sky-700",
  FINISHED: "tw-bg-emerald-100 tw-text-emerald-700",
  CANCELED: "tw-bg-rose-100 tw-text-rose-600",
  ADMIN_STOP: "tw-bg-red-100 tw-text-red-700",
  KAKAO_ADMIN_STOP: "tw-bg-red-100 tw-text-red-700",
};

// ── 메시지 유형 ───────────────────────────────────────────────

/** 메시지 유형 코드 → 한글 레이블. 목록·예약 페이지에서 공통 사용 */
export const MESSAGE_TYPE_LABEL: Record<string, string> = {
  BASIC_TEXT_MESSAGE: "기본 텍스트",
  WIDE_MESSAGE: "와이드 이미지",
  WIDE_LIST_MESSAGE: "와이드 리스트",
  CAROUSEL_FEED_MESSAGE: "캐러셀 피드",
  CAROUSEL_COMMERCE_MESSAGE: "캐러셀 커머스",
};

// ── 날짜 필터 ─────────────────────────────────────────────────

/** 목록 페이지 날짜 필터 기준 → 한글 레이블 */
export const DATE_FILTER_TYPE_LABEL: Record<string, string> = {
  sendDate: "발송 예정일",
  createdDate: "등록일",
};

/**
 * 목록 페이지 날짜 프리셋 옵션 (4개).
 * 오늘 / 어제 / 최근 7일 / 최근 30일
 */
export const DATE_PRESET_OPTIONS = [
  { value: "TODAY", label: "오늘" },
  { value: "YESTERDAY", label: "어제" },
  { value: "LAST_7DAY", label: "최근 7일" },
  { value: "LAST_30DAY", label: "최근 30일" },
];

/**
 * 리포트 페이지 날짜 프리셋 옵션 (3개).
 * 목록 페이지보다 항목이 적고 라벨 표기도 간략합니다.
 * (오늘 / 어제 / 7일 — "최근 30일" 없음)
 */
export const REPORT_DATE_PRESET_OPTIONS = [
  { value: "TODAY", label: "오늘" },
  { value: "YESTERDAY", label: "어제" },
  { value: "LAST_7DAY", label: "7일" },
];

// ── 외부 링크 ─────────────────────────────────────────────────

/**
 * 카카오 모먼트 발송 지연 공식 안내 URL.
 * 예약 페이지의 지연 안내 링크에서 사용합니다.
 */
export const KAKAO_DELAY_NOTICE_URL =
  "https://kakaobusiness.gitbook.io/main/ad/moment/messagead/channelmessage#id-4-3";
